import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const FAQS = [
  {
    category: "Orders",
    items: [
      ["How can I place an order?", "Pick a gift, personalize it if you like, add it to your cart and proceed to checkout. Sign in, choose your delivery address and payment method, and place the order — you'll see a confirmation with your Order ID right away."],
      ["How can I track my order?", "Use the Track Order page with your Order ID and email, or sign in and open My Orders for a live delivery timeline."],
      ["Can I cancel my order?", "Yes — orders can be cancelled free of charge until they are shipped. Contact us at customizegiftonline@gmail.com or call 9899534126 with your Order ID. Personalized gifts already in production may not be cancellable."],
    ],
  },
  {
    category: "Personalization",
    items: [
      ["Can I personalize a product?", "Yes! Products marked with a 'Make it personal' panel support custom names, messages and photos. Look for personalized mugs, photo frames, cushions and keepsakes in the Personalized Gifts category."],
      ["How do I upload my photo?", "Open a personalized product, click 'Select Image' in the Make it personal panel, and choose a JPG, PNG or WEBP photo from your device. You'll see a preview instantly and can change or remove it before adding to cart."],
      ["What photo quality works best?", "A clear, well-lit photo of at least 800×800 pixels prints best. We accept images up to 8MB and automatically optimize them for printing."],
    ],
  },
  {
    category: "Delivery",
    items: [
      ["How long does delivery take?", "Standard delivery takes 3–5 business days (free above ₹999), Express takes 1–2 business days (₹99), and Same Day is available in select Delhi, Mumbai and Bengaluru pincodes (₹149, order before 6 PM). Personalized gifts may need 1–2 extra days for printing."],
      ["Do you deliver to my city?", "Enter your pincode on any product page or at checkout — we instantly show what's available for your area."],
      ["Is same-day delivery available?", "Yes, for select products and pincodes in Delhi, Mumbai and Bengaluru when you order before 6 PM. The product page pincode checker will confirm."],
    ],
  },
  {
    category: "Payments",
    items: [
      ["What payment methods are available?", "We accept Cash on Delivery and are bringing secure online payments (UPI, cards, net banking via Razorpay) very soon. Online payment availability is shown at checkout."],
      ["Is it safe to pay online?", "Yes. Online payments are processed by Razorpay over encrypted connections — we never see or store your card details."],
      ["Do you offer Cash on Delivery?", "Yes, COD is available on most orders. Simply choose 'Cash on Delivery' at the payment step."],
    ],
  },
  {
    category: "Returns",
    items: [
      ["Can personalized products be returned?", "Because personalized gifts are made uniquely for you, they can't be returned or exchanged — unless they arrive damaged, defective or incorrect, in which case we replace or refund them."],
      ["What if my gift arrives damaged?", "Share a photo of the damaged item within 24 hours of delivery at customizegiftonline@gmail.com, and we'll arrange a free replacement or a full refund."],
    ],
  },
  {
    category: "Refunds",
    items: [
      ["How long do refunds take?", "Approved refunds are processed within 5–7 business days to your original payment method, or as bank transfer for COD orders."],
      ["Where is my refund?", "Once processed, we'll confirm by email. If it's been more than 7 business days, contact us with your Order ID and we'll trace it immediately."],
    ],
  },
  {
    category: "Corporate Gifts",
    items: [
      ["Do you handle bulk corporate orders?", "Yes — personalized mugs, frames, hampers and keepsakes with your company branding, at volume pricing. Write to customizegiftonline@gmail.com with quantities for a quote."],
      ["Can you print our company logo?", "Absolutely. Share your logo after placing a corporate enquiry and our team will prepare a digital proof before production."],
    ],
  },
];

export default function FaqPage() {
  const [open, setOpen] = useState(null);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 lg:py-16" data-testid="faq-page">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B45309]">Help Center</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 mt-3 tracking-tight">Frequently Asked Questions</h1>
      <p className="text-stone-500 mt-2">Quick answers about orders, personalization, delivery and payments.</p>

      <div className="mt-10 space-y-8">
        {FAQS.map((section) => (
          <div key={section.category}>
            <h2 className="font-serif text-xl font-bold text-stone-900 mb-3">{section.category}</h2>
            <div className="space-y-2.5">
              {section.items.map(([q, a]) => {
                const key = `${section.category}:${q}`;
                const isOpen = open === key;
                return (
                  <div key={q} className="rounded-2xl bg-white border border-[#E9E2D7] overflow-hidden" data-testid={`faq-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <button onClick={() => setOpen(isOpen ? null : key)} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left" data-testid={`faq-toggle-${key.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} aria-expanded={isOpen}>
                      <span className="text-sm font-bold text-stone-800">{q}</span>
                      <ChevronDown size={16} className={`text-[#B45309] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
                          <p className="px-5 pb-5 text-sm text-stone-600 leading-relaxed">{a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 p-6 rounded-2xl bg-[#F9F3EA] border border-[#E9E2D7] text-center">
        <p className="font-serif text-lg font-bold text-stone-900">Still stuck?</p>
        <p className="text-sm text-stone-500 mt-1">Write to <a href="mailto:customizegiftonline@gmail.com" className="font-bold text-[#B45309] hover:underline">customizegiftonline@gmail.com</a> or call <a href="tel:9899534126" className="font-bold text-[#B45309] hover:underline">9899534126</a></p>
      </div>
    </div>
  );
}
